import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Terminal, Download, Copy, Trash2, RotateCcw, CheckCircle, XCircle, FileText, Save } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import websocketService from '../services/websocket';
import apiService from '../services/api';

const ConsoleViewer = ({ jobId, savedConfig, onRetry }) => {
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [jobState, setJobState] = useState('waiting');
  const [autoScroll, setAutoScroll] = useState(true);
  const [compactView, setCompactView] = useState(true);
  const [showSaveCluster, setShowSaveCluster] = useState(false);
  const [clusterName, setClusterName] = useState('');
  const [clusterSaved, setClusterSaved] = useState(false);
  const consoleRef = useRef(null);

  const isNoiseLog = useCallback((message) => {
    if (!message) return true;
    const trimmed = message.trim();
    if (!trimmed) return true;
    if (/^[\s.]+$/.test(trimmed)) return true;
    if (/^\.+$/.test(trimmed)) return true;
    if (/^\s*\d+%\s+[\d.]+[KMG]?\s+[\d.]+[sm]?/.test(trimmed)) return true;
    if (/^[\s\-\\|/]+$/.test(trimmed)) return true;
    if (/^\[ERROR\]\s*[\s.]*$/.test(trimmed)) return true;
    if (/\[ERROR\]:\s*\.+/.test(trimmed)) return true;
    return false;
  }, []);

  const cleanLogMessage = useCallback((message) => {
    return message
      .replace(/\[[\d\-T:.Z]+\]\s*\[[A-Z]+\]\s*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const addLog = useCallback((message, level = 'info') => {
    if (isNoiseLog(message)) return;
    const timestamp = new Date().toISOString();
    setLogs(prev => [...prev, { message: cleanLogMessage(message), level, timestamp }]);
  }, [isNoiseLog, cleanLogMessage]);

  useEffect(() => {
    if (!jobId) return;

    setLogs([]);
    setProgress(0);
    setJobState('waiting');
    setClusterSaved(false);

    websocketService.connect();
    websocketService.joinJob(jobId);

    const handleLog = (data) => {
      if (data.jobId === jobId) {
        addLog(data.message, data.level);
      }
    };

    const handleProgress = (data) => {
      if (data.jobId === jobId) {
        setProgress(data.progress);
        if (data.status) {
          addLog(`Progress: ${data.progress}% - ${data.status}`, 'info');
        }
      }
    };

    const handleStateChange = (data) => {
      if (data.jobId === jobId) {
        setJobState(data.state);
        addLog(`Job state changed to: ${data.state}`, 'info');
      }
    };

    const handleJobComplete = (data) => {
      if (data.jobId === jobId) {
        addLog('✅ Installation completed successfully!', 'success');
        setJobState('completed');
        setProgress(100);
        setShowSaveCluster(true);
        setClusterName(`Cluster-${new Date().toISOString().split('T')[0]}`);
      }
    };

    const handleJobFailed = (data) => {
      if (data.jobId === jobId) {
        addLog(`❌ Installation failed: ${data.error}`, 'error');
        setJobState('failed');
      }
    };

    websocketService.onLog(handleLog);
    websocketService.onProgress(handleProgress);
    websocketService.onStateChange(handleStateChange);
    websocketService.onJobComplete(handleJobComplete);
    websocketService.onJobFailed(handleJobFailed);

    return () => {
      websocketService.leaveJob(jobId);
      websocketService.off('log');
      websocketService.off('progress');
      websocketService.off('state-change');
      websocketService.off('job-complete');
      websocketService.off('job-failed');
    };
  }, [jobId, addLog]);

  useEffect(() => {
    if (autoScroll && consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const clearLogs = () => setLogs([]);

  const getFormattedLogs = () => {
    return logs.map(log => 
      `${log.timestamp} [${log.level.toUpperCase()}] ${log.message}`
    ).join('\n');
  };

  const downloadLogs = () => {
    const logText = getFormattedLogs();
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kafka-installation-${jobId}-${new Date().toISOString().split('T')[0]}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyLogs = () => {
    navigator.clipboard.writeText(getFormattedLogs());
  };

  const handleSaveCluster = async () => {
    try {
      const clusterData = {
        name: clusterName,
        nodes: savedConfig?.nodes?.map((n, i) => ({
          host: n.host,
          privateIp: n.privateIp || n.host,
          brokerId: i
        })) || [],
        version: savedConfig?.version || '3.6.1',
        mode: savedConfig?.useKRaft ? 'KRaft' : 'Legacy',
        installPath: savedConfig?.installPath || '/opt/kafka',
        dataPath: savedConfig?.dataPath || '/var/lib/kafka',
        createdAt: new Date().toISOString()
      };
      await apiService.saveCluster(clusterData);
      setClusterSaved(true);
      setShowSaveCluster(false);
    } catch (err) {
      console.error('Failed to save cluster:', err);
    }
  };

  const getLogClass = (level) => {
    switch (level) {
      case 'error': return 'text-red-400';
      case 'warn': return 'text-amber-400';
      case 'success': return 'text-green-400';
      default: return 'text-slate-300';
    }
  };

  const getStateBadgeVariant = () => {
    switch (jobState) {
      case 'waiting': return 'warning';
      case 'active': return 'default';
      case 'completed': return 'success';
      case 'failed': return 'destructive';
      default: return 'secondary';
    }
  };

  const getProgressColor = () => {
    switch (jobState) {
      case 'completed': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-primary';
    }
  };

  const displayLogs = compactView 
    ? logs.filter(log => {
        const msg = log.message.toLowerCase();
        return log.level !== 'info' || 
               msg.includes('step') || msg.includes('✓') || msg.includes('✗') ||
               msg.includes('✅') || msg.includes('❌') || msg.includes('⚠') ||
               msg.includes('starting') || msg.includes('completed') ||
               msg.includes('failed') || msg.includes('connected') ||
               msg.includes('configured') || msg.includes('installed') ||
               msg.includes('detected') || msg.includes('node') || msg.includes('cluster');
      })
    : logs;

  return (
    <div className="rounded-lg border border-border/50 overflow-hidden">
      <div className="bg-slate-900 text-white p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Terminal className="h-5 w-5" />
            <div>
              <span className="text-sm font-semibold">Installation Console</span>
              {jobId && <span className="text-xs text-slate-400 ml-2 font-mono">Job #{jobId}</span>}
            </div>
          </div>
          <Badge variant={getStateBadgeVariant()} className="text-xs h-6">
            {jobState === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
            {jobState === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
            {jobState.toUpperCase()}
          </Badge>
        </div>

        <div className="flex items-center gap-3">
          <Progress value={progress} className="flex-1 h-2 bg-slate-700" indicatorClassName={getProgressColor()} />
          <span className="text-xs font-semibold min-w-[40px] text-right">{progress}%</span>
        </div>

        {jobState === 'failed' && savedConfig && (
          <div className="flex items-center justify-between p-3 bg-red-500/20 rounded-lg border border-red-500/30">
            <div>
              <p className="text-sm font-medium text-red-300">Installation Failed</p>
              <p className="text-xs text-red-400/80">Your configuration has been saved. You can retry.</p>
            </div>
            <Button onClick={onRetry} variant="destructive" size="sm" className="h-8 text-xs">
              <RotateCcw className="h-4 w-4 mr-1" />Retry
            </Button>
          </div>
        )}

        {jobState === 'completed' && (
          <div className="flex items-center justify-between p-3 bg-green-500/20 rounded-lg border border-green-500/30">
            <div>
              <p className="text-sm font-medium text-green-300">Installation Successful!</p>
              <p className="text-xs text-green-400/80">
                {clusterSaved ? 'Cluster saved! Go to Clusters tab.' : 'Save your cluster to manage it.'}
              </p>
            </div>
            <div className="flex gap-2">
              {!clusterSaved && (
                <Button onClick={() => setShowSaveCluster(true)} size="sm" className="h-8 text-xs">
                  <Save className="h-4 w-4 mr-1" />Save Cluster
                </Button>
              )}
              <Button onClick={downloadLogs} variant="outline" size="sm" className="h-8 text-xs border-green-500/50 text-green-300 hover:bg-green-500/20">
                <FileText className="h-4 w-4 mr-1" />Save Logs
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-slate-800 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Checkbox id="autoScroll" checked={autoScroll} onCheckedChange={setAutoScroll} className="h-4 w-4 border-slate-500" />
            <Label htmlFor="autoScroll" className="text-xs text-slate-300 cursor-pointer">Auto-scroll</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="compactView" checked={compactView} onCheckedChange={setCompactView} className="h-4 w-4 border-slate-500" />
            <Label htmlFor="compactView" className="text-xs text-slate-300 cursor-pointer">Compact</Label>
          </div>
          <span className="text-xs text-slate-500">{displayLogs.length} / {logs.length} entries</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={copyLogs} disabled={logs.length === 0} className="h-7 px-2 text-xs text-slate-300 hover:text-white hover:bg-slate-700">
            <Copy className="h-3.5 w-3.5 mr-1" />Copy
          </Button>
          <Button variant="ghost" size="sm" onClick={downloadLogs} disabled={logs.length === 0} className="h-7 px-2 text-xs text-slate-300 hover:text-white hover:bg-slate-700">
            <Download className="h-3.5 w-3.5 mr-1" />Download
          </Button>
          <Button variant="ghost" size="sm" onClick={clearLogs} disabled={logs.length === 0} className="h-7 px-2 text-xs text-slate-300 hover:text-white hover:bg-slate-700">
            <Trash2 className="h-3.5 w-3.5 mr-1" />Clear
          </Button>
        </div>
      </div>

      <div ref={consoleRef} className="console-output bg-slate-950 text-slate-300 p-3 h-[320px] overflow-y-auto font-mono text-xs">
        {displayLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <Terminal className="h-10 w-10 mb-3" />
            <p className="text-sm">Waiting for installation to start...</p>
          </div>
        ) : (
          displayLogs.map((log, index) => (
            <div key={index} className={cn("py-0.5 flex gap-3", getLogClass(log.level))}>
              <span className="text-slate-500 shrink-0 text-[11px]">{new Date(log.timestamp).toLocaleTimeString()}</span>
              <span className="break-all">{log.message}</span>
            </div>
          ))
        )}
      </div>

      <Dialog open={showSaveCluster} onOpenChange={setShowSaveCluster}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Save Cluster</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <p className="text-xs text-muted-foreground">Save this cluster to manage it later.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Cluster Name</Label>
              <Input value={clusterName} onChange={(e) => setClusterName(e.target.value)} placeholder="My Kafka Cluster" className="h-9 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveCluster(false)} size="sm" className="h-8 text-xs">Skip</Button>
            <Button onClick={handleSaveCluster} disabled={!clusterName} size="sm" className="h-8 text-xs">
              <Save className="h-3.5 w-3.5 mr-1" />Save Cluster
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsoleViewer;
